import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreVertical, UserPlus, Edit3, Trash2, Flag, X } from 'lucide-react';

const GroupSettingsDropdown = ({
  group,
  isOwner,
  isMember,
  isAdmin,
  onEdit,
  onDelete,
  onReport,
  onLeave,
  onInvite,
  onJoin,
}) => {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAction = (action, data) => {
    setShowDropdown(false);
    ({
      edit: onEdit,
      delete: onDelete,
      report: onReport,
      leave: onLeave,
      invite: onInvite,
      join: onJoin,
    }[action])?.(data);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        aria-haspopup="menu"
        aria-expanded={showDropdown}
        aria-label={t('groups.settingsDropdown.groupSettingsAria')}
        onClick={() => setShowDropdown(!showDropdown)}
        className="p-2 bg-black/20 hover:bg-black/40 rounded-xl transition-all duration-300 border border-white/10"
      >
        <MoreVertical className="text-gray-300" size={20} />
      </button>

      {showDropdown && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50"
        >
          <div className="p-2">
            {/* Join (public groups, non-members) */}
            {!isMember && group?.privacy === 'public' && (
              <button
                onClick={() => handleAction('join', group)}
                className="w-full px-3 py-2 text-left text-green-700 dark:text-green-400 hover:text-green-300 hover:bg-green-900/20 rounded-lg flex items-center space-x-2"
              >
                <UserPlus size={16} />
                <span>{t('groups.settingsDropdown.joinGroup')}</span>
              </button>
            )}

            {/* Owner options */}
            {isOwner && (
              <>
                <button
                  onClick={() => handleAction('edit', group)}
                  className="w-full px-3 py-2 text-left text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex items-center space-x-2"
                >
                  <Edit3 size={16} />
                  <span>{t('groups.settingsDropdown.editGroup')}</span>
                </button>
                <button
                  onClick={() => handleAction('invite')}
                  className="w-full px-3 py-2 text-left text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex items-center space-x-2"
                >
                  <UserPlus size={16} />
                  <span>{t('groups.settingsDropdown.inviteMembers')}</span>
                </button>
                <div className="border-t border-gray-200 dark:border-gray-700 my-2" />
                <button
                  onClick={() => handleAction('delete', group)}
                  className="w-full px-3 py-2 text-left text-red-700 dark:text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg flex items-center space-x-2"
                >
                  <Trash2 size={16} />
                  <span>{t('groups.settingsDropdown.deleteGroup')}</span>
                </button>
              </>
            )}

            {/* Admin (non-owner) */}
            {isAdmin && !isOwner && (
              <>
                <button
                  onClick={() => handleAction('invite')}
                  className="w-full px-3 py-2 text-left text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex items-center space-x-2"
                >
                  <UserPlus size={16} />
                  <span>{t('groups.settingsDropdown.inviteMembers')}</span>
                </button>
                <button
                  onClick={() => handleAction('leave', group)}
                  className="w-full px-3 py-2 text-left text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20 rounded-lg flex items-center space-x-2"
                >
                  <X size={16} />
                  <span>{t('groups.settingsDropdown.leaveGroup')}</span>
                </button>
              </>
            )}

            {/* Regular member (not owner, not admin) */}
            {isMember && !isOwner && !isAdmin && (
              <button
                onClick={() => handleAction('leave', group)}
                className="w-full px-3 py-2 text-left text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20 rounded-lg flex items-center space-x-2"
              >
                <X size={16} />
                <span>{t('groups.settingsDropdown.leaveGroup')}</span>
              </button>
            )}

            {/* Report (everyone except owner) */}
            {!isOwner && (
              <button
                onClick={() => handleAction('report', group)}
                className="w-full px-3 py-2 text-left text-red-700 dark:text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg flex items-center space-x-2"
              >
                <Flag size={16} />
                <span>{t('groups.settingsDropdown.reportGroup')}</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupSettingsDropdown;
